import React from 'react';
import { Bot } from 'lucide-react';

/**
 * A provider's brand mark, at a consistent optical size.
 *
 * Lifted out of ModelsPanel so every picker that names a provider shows the
 * same mark. A dropdown listing "azure_openai" as bare text next to a panel
 * showing the Azure logo reads as two different things.
 *
 * Logos live under public/icons/model_icons. openai_compat has no brand of its
 * own — by definition it is somebody else's endpoint — so it falls through to
 * the generic mark.
 */
const PROVIDER_ICON = {
  openai: '/icons/model_icons/openai.svg',
  anthropic: '/icons/model_icons/anthropic.png',
  gemini: '/icons/model_icons/gemini.png',
  mistral: '/icons/model_icons/mistralai.png',
  fireworks: '/icons/model_icons/fireworksai.png',
  groq: '/icons/model_icons/groq.png',
  deepseek: '/icons/model_icons/deepseek.png',
  xai: '/icons/model_icons/grok.png',
  azure_openai: '/icons/model_icons/azureai.png',
  vertex_ai: '/icons/model_icons/vertexai.png',
  bedrock: '/icons/model_icons/bedrock.png',
  trodo: '/trodologo.svg',
  jev: '/icons/model_icons/typesafe.png',
};

/** Providers with no logo asset show their initial in the neutral circle. */
const PROVIDER_LETTER = {};

/** Some PNGs carry heavy internal whitespace, so the mark reads small. */
const PROVIDER_LOGO_SCALE = { azure_openai: 1.35 };

/** Dark-on-transparent marks need a white pill to survive dark mode. */
const PROVIDER_NEEDS_WHITE_BG = new Set(['openai', 'xai', 'deepseek']);

/** Human labels, so the UI never shows a snake_case key. */
export const PROVIDER_LABEL = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Google Gemini',
  mistral: 'Mistral',
  fireworks: 'Fireworks',
  groq: 'Groq',
  deepseek: 'DeepSeek',
  xai: 'xAI',
  azure_openai: 'Azure OpenAI',
  vertex_ai: 'Vertex AI',
  bedrock: 'Bedrock',
  openai_compat: 'OpenAI-compatible',
  jev: 'Jev',
  trodo: 'Hartley',
};

export function providerLabel(provider) {
  return PROVIDER_LABEL[provider] || provider || 'Unknown';
}

export default function ProviderLogo({ provider, size = 18 }) {
  const src = PROVIDER_ICON[provider];

  if (!src) {
    const letter = PROVIDER_LETTER[provider];
    return (
      <span
        aria-hidden
        style={{
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: '1px solid var(--border-default)',
          borderRadius: '50%',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
      >
        {letter ? (
          <span style={{ fontSize: Math.max(8, Math.round(size * 0.55)), lineHeight: 1, fontWeight: 600, color: 'var(--text-muted)' }}>{letter}</span>
        ) : (
          <Bot style={{ width: size - 6, height: size - 6, color: 'var(--text-muted)' }} />
        )}
      </span>
    );
  }

  const scale = PROVIDER_LOGO_SCALE[provider] || 1;
  const needsWhiteBg = PROVIDER_NEEDS_WHITE_BG.has(provider);

  return (
    <span
      style={{
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        borderRadius: '50%',
        background: needsWhiteBg ? '#ffffff' : 'transparent',
        padding: needsWhiteBg ? 2 : 0,
      }}
    >
      <img
        src={src}
        alt=""
        style={{
          width: `${(size - (needsWhiteBg ? 4 : 0)) * scale}px`,
          height: `${(size - (needsWhiteBg ? 4 : 0)) * scale}px`,
          objectFit: 'contain',
        }}
      />
    </span>
  );
}
